import { useMemo } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import type { TagResumo } from '@/api/tags';
import { queryKeys } from '@/lib/pwa/queryConfig';

type ComTags = { id: string; tags?: TagResumo[]; etiquetas?: TagResumo[] };

/**
 * Troca as tags de um registro em qualquer forma de cache: lista simples, página
 * (`{ items }`), lista infinita (`{ pages: [{ items }] }`) ou o próprio registro.
 * O artefato guarda as tags em `etiquetas` e os nomes em `tags` (contrato antigo).
 */
function trocar(dado: unknown, id: string, tags: TagResumo[], campo: 'tags' | 'etiquetas'): unknown {
  if (!dado || typeof dado !== 'object') return dado;
  if (Array.isArray(dado)) {
    let mudou = false;
    const lista = dado.map((item) => {
      if ((item as ComTags)?.id !== id) return item;
      mudou = true;
      return comTags(item as ComTags, tags, campo);
    });
    return mudou ? lista : dado;
  }
  const registro = dado as ComTags & { items?: unknown; pages?: unknown[] };
  if (registro.id === id) return comTags(registro, tags, campo);
  if (Array.isArray(registro.items)) {
    const items = trocar(registro.items, id, tags, campo);
    return items === registro.items ? dado : { ...registro, items };
  }
  if (Array.isArray(registro.pages)) {
    const pages = registro.pages.map((p) => trocar(p, id, tags, campo));
    return pages.some((p, i) => p !== registro.pages![i]) ? { ...registro, pages } : dado;
  }
  return dado;
}

function comTags(item: ComTags, tags: TagResumo[], campo: 'tags' | 'etiquetas'): ComTags {
  return campo === 'etiquetas' ? { ...item, etiquetas: tags, tags: tags.map((t) => t.nome) as never } : { ...item, tags };
}

/** Os caches de uma tela, para o otimista das tags. */
export interface CacheDeTags {
  /** Escreve o conjunto de tags do registro em todos os caches dos prefixos. */
  escrever: (id: string, tags: TagResumo[]) => void;
  /**
   * Cancela as leituras em voo desses caches. Sem isto, um GET lido ANTES do PUT
   * chegava depois dele e regravava a lista antiga por cima do otimista — a tag
   * aplicada sumia da tela até o próximo refetch.
   */
  cancelar: () => Promise<void>;
}

/**
 * `CacheDeTags` dos prefixos dados (lista, filtros, detalhe). Os prefixos são
 * constantes de módulo nos chamadores.
 */
export function useTagsNoCache(prefixos: readonly QueryKey[], campo: 'tags' | 'etiquetas' = 'tags'): CacheDeTags {
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      escrever: (id: string, tags: TagResumo[]) => {
        for (const queryKey of prefixos) {
          queryClient.setQueriesData({ queryKey }, (dado: unknown) => trocar(dado, id, tags, campo));
        }
      },
      cancelar: async () => {
        await Promise.all(prefixos.map((queryKey) => queryClient.cancelQueries({ queryKey })));
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, campo],
  );
}

/** Prefixos dos caches que mostram as tags de um processo (lista e cabeçalho dos autos). */
export const CACHES_DE_PROCESSO: readonly QueryKey[] = [queryKeys.processos.all];
